import React, { lazy, Suspense, useCallback, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ArrowBack, ArrowUpward, AutoAwesome, DeleteForever, RotateLeft, Save } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, Link, Typography, Zoom } from '@mui/material'

import GRCircularProgress from '../../../components/GRCircularProgress/GRCircularProgress'
import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { GameCardContent } from '../../../features/game/components/GameCard/GameCard'
import { MotivationKey, MotivationType } from '../../../features/market-explorer/types/MotivationType'
import MotivationArchetypeBadge from '../../../features/motivations/components/MotivationArchetypeBadge/MotivationArchetypeBadge'
import { MotivationTypeBadge } from '../../../features/motivations/components/MotivationTypeBadge/MotivationTypeBadge'
import { motivationArchetypeImages } from '../../../features/motivations/utils/MotivationArchetypeImages'
import { MotivationIcon } from '../../../features/motivations/utils/svgs'
import { displaySnackBar } from '../../../features/snackbar'
import { useAppDispatch } from '../../../hooks/storeHooks'
import { useRequiredParams } from '../../../hooks/useRequiredParams'
import languageService from '../../../services/LanguageService'
import { internalCoreApi, useGetTrackedGameEventQuery, useGetTrackedGameQuery } from '../../api/internal'
import { useGetMotivationAnalysisQuery, useLazyGetMotivationDescriptionsStructuredQuery, usePostLiveEventAnalysisMutation } from '../../api/internal-combined'
import { ActionButton } from '../../components/ActionButton/ActionButton'
import { Analyst } from '../../features/live-events-tracking/components/TrackedGamesTable/TrackedGamesTable'
import { LiveEventAnalysisMutation } from '../../types/types'
import './TrackedGameEventAnalysisPage.scss'

const HTMLEditor = lazy(() => import('../../components/HTMLEditor/HTMLEditor').then((module) => ({ default: module.HTMLEditor })))
const yOffset = -105

const TrackedGameEventAnalysisPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { trackedGameId, eventId } = useRequiredParams<{ trackedGameId: string; eventId: string }>()
  const trackedGameQuery = useGetTrackedGameQuery(trackedGameId)
  const trackedGameEventQuery = useGetTrackedGameEventQuery(eventId, { skip: !eventId })
  const navigate = useNavigate()

  const [
    triggerStructuredDescriptions,
    {
      data: motivationDescriptionsStructured,
      isLoading: isLoadingStructuredDescriptions,
      isFetching: isFetchingStructuredDescriptions,
      isError: isErrorStructuredDescriptions,
    },
  ] = useLazyGetMotivationDescriptionsStructuredQuery()

  const [postLiveEventData, { isError: isErrorPostLiveEventData, isSuccess: isSuccessPostLiveEventData, isLoading: isSavingLiveEventData }] =
    usePostLiveEventAnalysisMutation()
  const { data: motivationAnalysis } = useGetMotivationAnalysisQuery({ eventId }, { skip: !eventId })

  const getMotivationName = useCallback((key: MotivationKey | MotivationType) => {
    return languageService.getTranslation('motivations', key)
  }, [])

  useEffect(() => {
    if (isErrorStructuredDescriptions) {
      dispatch(displaySnackBar({ message: t('internal-live-events:error_could_not_load_openai_description'), severity: 'error', open: true }))
    }

    if (isErrorPostLiveEventData) {
      dispatch(displaySnackBar({ message: t('internal-live-events:error_could_not_save_motivations'), severity: 'error', open: true }))
    }

    if (isSuccessPostLiveEventData) {
      dispatch(displaySnackBar({ message: t('internal-live-events:success_motivations_saved'), severity: 'success', open: true }))
    }
  }, [dispatch, t, isErrorStructuredDescriptions, isErrorPostLiveEventData, isSuccessPostLiveEventData])

  const defaultValues = useMemo(
    () => ({
      eventId: eventId,
      motivations: [],
      archetypes: [],
    }),
    [eventId]
  )

  const descriptionForm = useForm<LiveEventAnalysisMutation>({
    defaultValues,
  })

  const {
    fields: fieldsMotivations,
    append: appendMotivation,
    remove: removeMotivation,
  } = useFieldArray({
    control: descriptionForm.control,
    name: 'motivations',
  })

  const {
    fields: fieldsArchetypes,
    append: appendArchetype,
    remove: removeArchetype,
  } = useFieldArray({
    control: descriptionForm.control,
    name: 'archetypes',
  })

  useEffect(() => {
    if (motivationAnalysis) {
      descriptionForm.reset({
        eventId: eventId,
        motivations: motivationAnalysis.motivations || [],
        archetypes: motivationAnalysis.archetypes || [],
      })
    }
  }, [motivationAnalysis, eventId, descriptionForm])

  const isActive = useCallback(
    (motivationOrActhetypeKey: MotivationKey | MotivationType) => {
      const values = descriptionForm.getValues()
      return (
        !!values.motivations.find((motivation) => motivation.key === motivationOrActhetypeKey) ||
        !!values.archetypes.find((archetype) => archetype.key === motivationOrActhetypeKey)
      )
    },
    [descriptionForm]
  )

  const onSubmit = (data: LiveEventAnalysisMutation) => {
    postLiveEventData(data)
      .unwrap()
      .then(() => {
        dispatch(internalCoreApi.util.invalidateTags([{ type: 'trackedGameEventsByAllDates', id: trackedGameId }]))
        navigate(-1)
      })
  }

  const handleBackToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleGenerateStructuredDescriptions = useCallback(() => {
    if (!trackedGameEventQuery.data?.event.comment.content.comment?.en) {
      dispatch(displaySnackBar({ message: 'No description found', severity: 'error', open: true }))
      return
    }

    triggerStructuredDescriptions({ description: trackedGameEventQuery.data?.event.comment.content.comment?.en })
  }, [trackedGameEventQuery.data?.event.comment.content.comment?.en, triggerStructuredDescriptions, dispatch])

  const isLoadingDescriptions = isLoadingStructuredDescriptions || isFetchingStructuredDescriptions

  useEffect(() => {
    if (motivationDescriptionsStructured) {
      const newValues = {
        ...defaultValues,
        motivations: motivationDescriptionsStructured.motivationSuggestions.map((motivation) => ({
          key: motivation.motivation,
          description: motivation.reasoning,
        })),
        archetypes: motivationDescriptionsStructured.archetypeSuggestions.map((archetype) => ({
          key: archetype.archetype,
          description: archetype.reasoning,
        })),
      }

      descriptionForm.reset(newValues)
    }
  }, [motivationDescriptionsStructured, descriptionForm, defaultValues])

  const renderEditor = (field: any, index: number, type: 'motivations' | 'archetypes') => {
    const control = descriptionForm.control

    return (
      <Card key={field.id} sx={{ marginBottom: 2 }} id={`${type}-editor-${field.id}`}>
        <CardHeader
          title={
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Grid container direction="row" alignItems="center">
                  <Grid item marginRight={1} display="flex" alignContent="center">
                    {type === 'motivations' && <MotivationIcon motivation={field.key} />}
                    {type === 'archetypes' && <img height="24px" width="24px" src={motivationArchetypeImages[field.key]} alt={getMotivationName(field.key)} />}
                  </Grid>
                  <Grid item>
                    <Typography variant="h3">{getMotivationName(field.key)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container>
                  <Grid item>
                    <GRTooltip content={t('internal-common:remove_description')}>
                      <ActionButton
                        color="warning"
                        onClick={() => {
                          if (type === 'motivations') {
                            removeMotivation(index)
                          }

                          if (type === 'archetypes') {
                            removeArchetype(index)
                          }

                          handleBackToTop()
                        }}
                      >
                        <DeleteForever fontSize="small" />
                      </ActionButton>
                    </GRTooltip>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          }
        />
        <Divider />
        <CardContent className="MotivationEditor">
          <Controller
            name={`${type}.${index}.description`}
            control={control}
            render={({ field }) => (
              <Suspense fallback={<GRCircularProgress />}>
                <HTMLEditor
                  value={field.value}
                  onEditorChange={(content) =>
                    descriptionForm.setValue(`${type}.${index}.description`, content, {
                      shouldDirty: true,
                    })
                  }
                  init={{
                    height: 250,
                    menubar: false,
                    convert_urls: false,
                    block_formats: 'Paragraph=p;',
                    plugins: 'lists',
                    toolbar: 'undo redo removeformat | blocks blockquote | outdent indent | bold italic underline bullist | code',
                  }}
                />
              </Suspense>
            )}
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <Box className="tracked-game-event-analysis-page">
      <Card sx={{ marginBottom: 2 }}>
        <CardHeader
          title={
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>{trackedGameQuery.data?.game && <GameCardContent game={trackedGameQuery.data?.game} disableGameLink />}</Grid>
              <Grid item sx={{ transform: 'translateX(-25%)' }}>
                <Typography variant="h3">{trackedGameEventQuery.data?.event.name}</Typography>
              </Grid>
              {trackedGameEventQuery.data?.analyst && (
                <Grid item>
                  <Zoom in={!!trackedGameEventQuery.data?.analyst} timeout={500}>
                    <Box>
                      <Analyst analyst={trackedGameEventQuery.data?.analyst} avatarProps={{ sx: { width: 60, height: 60 } }} />
                    </Box>
                  </Zoom>
                </Grid>
              )}
            </Grid>
          }
        />
        <Divider />
        <CardContent>
          <Link
            underline="none"
            variant="h3"
            display="inline-block"
            mb={2}
            onClick={(e) => {
              e.preventDefault()
              const element = document.getElementById('motivations-section')
              if (element) {
                const y = element.getBoundingClientRect().top + window.scrollY + yOffset
                window.scrollTo({ top: y, behavior: 'smooth' })
              }
            }}
          >
            <Trans i18nKey="internal-common:motivations" />
          </Link>
          <Box>
            {Object.values(MotivationType).map((motivationType) => {
              return (
                <MotivationTypeBadge
                  key={motivationType}
                  description={fieldsMotivations.find((motivation) => motivation.key === motivationType)?.description}
                  type={motivationType}
                  active={isActive(motivationType)}
                  onSelect={(motivationType) => {
                    if (fieldsMotivations.some((motivation) => motivation.key === motivationType)) {
                      const field = fieldsMotivations.find((motivation) => motivation.key === motivationType)
                      if (!field) return

                      const element = document.getElementById(`motivations-editor-${field.id}`)
                      if (!element) return

                      const y = element.getBoundingClientRect().top + window.scrollY + yOffset
                      window.scrollTo({ top: y, behavior: 'smooth' })

                      const index = fieldsMotivations.findIndex((motivation) => motivation.key === motivationType)

                      if (!index) return
                      descriptionForm.setFocus(`motivations.${index}.description`, { shouldSelect: true })
                      return
                    }

                    appendMotivation({ key: motivationType as MotivationType, description: '' })
                  }}
                />
              )
            })}
          </Box>

          <Link
            underline="none"
            variant="h3"
            display="block"
            mt={2}
            mb={2}
            onClick={(e) => {
              e.preventDefault()
              const element = document.getElementById('archetypes-section')
              if (element) {
                const y = element.getBoundingClientRect().top + window.scrollY + yOffset
                window.scrollTo({ top: y, behavior: 'smooth' })
              }
            }}
          >
            <Trans i18nKey="internal-common:archetypes" />
          </Link>
          <Box>
            {Object.values(MotivationKey).map((motivationKey) => {
              return (
                <MotivationArchetypeBadge
                  key={motivationKey}
                  active={isActive(motivationKey)}
                  motivationKey={motivationKey}
                  description={fieldsArchetypes.find((archetype) => archetype.key === motivationKey)?.description}
                  onSelect={(motivationKey) => {
                    if (fieldsArchetypes.some((archetype) => archetype.key === motivationKey)) {
                      const field = fieldsArchetypes.find((archetype) => archetype.key === motivationKey)
                      if (!field) return

                      const element = document.getElementById(`archetypes-editor-${field.id}`)
                      if (!element) return

                      const y = element.getBoundingClientRect().top + window.scrollY + yOffset
                      window.scrollTo({ top: y, behavior: 'smooth' })
                      return
                    }

                    appendArchetype({ key: motivationKey as MotivationKey, description: '' })
                  }}
                />
              )
            })}
          </Box>
        </CardContent>
      </Card>

      <FormProvider {...descriptionForm}>
        <form onSubmit={descriptionForm.handleSubmit(onSubmit)}>
          {/* EDITORS */}
          {fieldsMotivations.length > 0 && (
            <Typography variant="h3" mb={2} mt={2} id="motivations-section">
              <Trans i18nKey="internal-common:motivations" />
            </Typography>
          )}
          {fieldsMotivations.map((field, index) => renderEditor(field, index, 'motivations'))}

          {fieldsArchetypes.length > 0 && (
            <Typography variant="h3" mb={2} id="archetypes-section">
              <Trans i18nKey="internal-common:archetypes" />
            </Typography>
          )}
          {fieldsArchetypes.map((field, index) => renderEditor(field, index, 'archetypes'))}

          {/* TOOLBAR */}
          <Card sx={{ marginTop: 2, position: 'sticky', bottom: 20, zIndex: 2 }} elevation={5}>
            <CardContent>
              <Grid container justifyContent="space-between">
                <Grid item display="flex" alignItems="center">
                  <Button variant="text" color="primary" onClick={() => window.history.back()} startIcon={<ArrowBack />}>
                    <Trans i18nKey={`internal-common:back`} />
                  </Button>

                  <Divider orientation="vertical" variant="middle" flexItem sx={{ marginRight: 2, marginLeft: 2 }} />

                  <GRTooltip content="Back to top">
                    <ActionButton onClick={handleBackToTop}>
                      <ArrowUpward fontSize="small" />
                    </ActionButton>
                  </GRTooltip>
                </Grid>

                <Grid item>
                  <LoadingButton
                    startIcon={<AutoAwesome />}
                    variant="contained"
                    color="primary"
                    loading={isLoadingDescriptions || isSavingLiveEventData}
                    loadingPosition="start"
                    onClick={handleGenerateStructuredDescriptions}
                  >
                    <Trans i18nKey={`internal-common:generate_motivations`} />
                  </LoadingButton>
                </Grid>

                <Grid item display="flex">
                  <Button
                    variant="text"
                    color="primary"
                    startIcon={<RotateLeft />}
                    onClick={() => descriptionForm.reset()}
                    disabled={!descriptionForm.formState.isDirty || isLoadingDescriptions}
                  >
                    <Trans i18nKey={`internal-common:reset`} />
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Save />}
                    sx={{ marginLeft: 2 }}
                    type="submit"
                    disabled={!descriptionForm.formState.isValid || isLoadingDescriptions || !descriptionForm.formState.isDirty}
                  >
                    <Trans i18nKey={`internal-common:save`} />
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      </FormProvider>
    </Box>
  )
}

export default TrackedGameEventAnalysisPage
